import type { ReactNode } from 'react'
import { Badge, Button, Input } from '@betstarters/ui'
import { Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * The pieces every catalogue page shares: the page heading, the filter bar, the
 * status badge, the error banner and the pagination footer. Each page keeps its
 * own table and forms — only the furniture lives here.
 */

export function PageHeading({
  title,
  subtitle,
  action,
}: {
  title: string
  subtitle: string
  action?: ReactNode
}) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h2 className="text-2xl font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {action}
    </div>
  )
}

export function CatalogToolbar({
  searchInput,
  onSearchInput,
  onSubmitSearch,
  searchPlaceholder,
  includeDisabled,
  onToggleDisabled,
  filtered,
  onClear,
  children,
}: {
  searchInput: string
  onSearchInput: (v: string) => void
  onSubmitSearch: () => void
  searchPlaceholder: string
  includeDisabled: boolean
  onToggleDisabled: () => void
  filtered: boolean
  onClear: () => void
  /** Extra filters (e.g. the parent-type selector) sit between search and toggle. */
  children?: ReactNode
}) {
  const { t } = useTranslation('app')
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <form
        className="relative max-w-sm flex-1"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmitSearch()
        }}
      >
        <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={searchInput}
          onChange={(e) => onSearchInput(e.target.value)}
          placeholder={searchPlaceholder}
          className="pl-8"
        />
      </form>
      {children}
      <Button variant={includeDisabled ? 'secondary' : 'outline'} size="sm" onClick={onToggleDisabled}>
        {t('common.includeDisabled')}
      </Button>
      {filtered && (
        <Button variant="ghost" size="sm" onClick={onClear}>
          <X />
          {t('common.clear')}
        </Button>
      )}
    </div>
  )
}

export function StatusBadge({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation('app')
  return (
    <Badge variant={disabled ? 'secondary' : 'default'}>
      {disabled ? t('common.status.disabled') : t('common.status.active')}
    </Badge>
  )
}

export function ErrorBanner({ message }: { message: string }) {
  if (!message) return null
  return <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">{message}</div>
}

export function Pagination({
  total,
  offset,
  pageSize,
  onOffset,
  label,
}: {
  total: number
  offset: number
  pageSize: number
  onOffset: (v: number) => void
  label: string
}) {
  const { t } = useTranslation('app')
  return (
    <div className="mt-4 flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={offset === 0}
          onClick={() => onOffset(Math.max(0, offset - pageSize))}
        >
          {t('common.previous')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={offset + pageSize >= total}
          onClick={() => onOffset(offset + pageSize)}
        >
          {t('common.next')}
        </Button>
      </div>
    </div>
  )
}
